import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ArchitecturalPlanPage } from '../../../../../shared/models/architectural-plan-page.model';

/**
 * A page selected in the drawer, paired with the input file it belongs to.
 *
 * Issue #1652: a bare page number is ambiguous in multi-file projects — two
 * files can both contain a page 5 — so every selection entry carries the
 * owning `fileId` and the selection is always resolved through this pair.
 */
export interface SelectedPageRef {
  /** `InputFileMetadata.file_id` of the file this page belongs to. */
  fileId: string;
  page: ArchitecturalPlanPage;
}

export interface SelectedPagesToolbarAction {
  type: 'reingest' | 'cancel' | 'clear-selection';
  pages?: SelectedPageRef[];
}

/** Floating toolbar shown when pages are selected in the drawer, offering bulk actions like delete or re-ingest. */
@Component({
  selector: 'app-selected-pages-toolbar',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, MatCheckboxModule],
  templateUrl: './selected-pages-toolbar.component.html',
  styleUrls: ['./selected-pages-toolbar.component.scss'],
})
export class SelectedPagesToolbarComponent implements OnInit, OnChanges {
  @Input() selectedPages: SelectedPageRef[] = [];
  @Input() visible = false;
  @Input() drawerOpened = true;
  @Output() action = new EventEmitter<SelectedPagesToolbarAction>();

  internalVisible = false;

  ngOnInit(): void {
    this.updateVisibility();
  }

  ngOnChanges(): void {
    this.updateVisibility();
  }

  private updateVisibility(): void {
    this.internalVisible = this.visible && this.selectedPages.length > 0;
  }

  onReingestPages(): void {
    if (this.selectedPages.length > 0) {
      this.action.emit({
        type: 'reingest',
        pages: [...this.selectedPages],
      });
    }
  }

  onCancel(): void {
    this.action.emit({
      type: 'cancel',
    });
  }

  onClearSelection(): void {
    this.action.emit({
      type: 'clear-selection',
    });
  }

  getSelectedPagesText(): string {
    const count = this.selectedPages.length;
    if (count === 0) return 'No pages selected';
    if (count === 1) return '1 page selected';
    return `${count} pages selected`;
  }

  /**
   * Page numbers of the current selection, sorted and de-duplicated per file.
   *
   * Issue #1652: when the selection spans more than one file the same page
   * number can legitimately appear twice, so entries are grouped by file to
   * keep the subtitle unambiguous.
   */
  getSelectedPageNumbers(): string {
    const byFile = new Map<string, number[]>();
    for (const ref of this.selectedPages) {
      const numbers = byFile.get(ref.fileId) ?? [];
      numbers.push(ref.page.getPageNumber());
      byFile.set(ref.fileId, numbers);
    }

    if (byFile.size <= 1) {
      return [...byFile.values()]
        .flat()
        .sort((a, b) => a - b)
        .join(', ');
    }

    return [...byFile.entries()]
      .map(([fileId, numbers]) => `File ${fileId}: ${numbers.sort((a, b) => a - b).join(', ')}`)
      .join(' · ');
  }
}
